
import React from 'react';
import { Mail, MapPin, Phone, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';

export const ImpressumPage: React.FC = () => {
    const { t } = useTranslation();

    return (
        <div className="pt-32 pb-20 bg-gray-50 min-h-screen">
            <div className="max-w-4xl mx-auto px-6">

                <Reveal>
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 tracking-tight">
                        {t('shared.legal.imprint.title')}
                    </h1>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="bg-white border border-gray-200 p-8 md:p-12 shadow-sm space-y-12">

                        {/* FIRMENANGABEN */}
                        <section>
                            <h2 className="text-sm font-bold text-clever-teal uppercase tracking-widest mb-6 border-b border-gray-100 pb-2">
                                {t('shared.legal.imprint.sections.company_info.title')}
                            </h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="font-bold text-gray-900 text-lg mb-1">
                                        {t('shared.legal.imprint.sections.company_info.name')}
                                    </h3>
                                    <div className="flex items-start gap-3 text-gray-600 mt-4 leading-relaxed">
                                        <MapPin size={20} className="shrink-0 mt-1 text-gray-400" />
                                        <span>
                                            {t('shared.legal.imprint.sections.company_info.address').split('\n').map((line, i) => (
                                                <React.Fragment key={i}>
                                                    {line}
                                                    {i === 0 && <br />}
                                                </React.Fragment>
                                            ))}
                                        </span>
                                    </div>
                                </div>
                                <div className="space-y-4 text-gray-600">
                                    <div className="flex flex-col">
                                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                            {t('shared.legal.imprint.sections.company_info.registration_number_label')}
                                        </span>
                                        <span className="font-mono">
                                            {t('shared.legal.imprint.sections.company_info.registration_number')}
                                        </span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                            {t('shared.legal.imprint.sections.company_info.court_label')}
                                        </span>
                                        <span>
                                            {t('shared.legal.imprint.sections.company_info.court')}
                                        </span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                            {t('shared.legal.imprint.sections.company_info.vat_label')}
                                        </span>
                                        <span className="font-mono">
                                            {t('shared.legal.imprint.sections.company_info.vat')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* KONTAKT */}
                        <section>
                            <h2 className="text-sm font-bold text-clever-teal uppercase tracking-widest mb-6 border-b border-gray-100 pb-2">
                                {t('shared.legal.imprint.sections.contact.title')}
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <a href="tel:+4366493367227" className="flex items-center gap-4 p-4 border border-gray-100 hover:border-clever-teal hover:bg-clever-teal/5 transition-colors group">
                                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 group-hover:text-clever-teal group-hover:bg-white transition-colors">
                                        <Phone size={18} />
                                    </div>
                                    <div>
                                        <span className="block text-xs font-bold text-gray-400 uppercase">
                                            {t('shared.legal.imprint.sections.contact.phone_label')}
                                        </span>
                                        <span className="font-medium text-gray-900">+43 664 933 67 227</span>
                                    </div>
                                </a>
                                <a href="mailto:office@cleverdigital.at" className="flex items-center gap-4 p-4 border border-gray-100 hover:border-clever-teal hover:bg-clever-teal/5 transition-colors group">
                                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 group-hover:text-clever-teal group-hover:bg-white transition-colors">
                                        <Mail size={18} />
                                    </div>
                                    <div>
                                        <span className="block text-xs font-bold text-gray-400 uppercase">
                                            {t('shared.legal.imprint.sections.contact.email_label')}
                                        </span>
                                        <span className="font-medium text-gray-900">office@cleverdigital.at</span>
                                    </div>
                                </a>
                            </div>
                        </section>

                        {/* UNTERNEHMENSGEGENSTAND & MITGLIEDSCHAFTEN */}
                        <section>
                            <h2 className="text-sm font-bold text-clever-teal uppercase tracking-widest mb-6 border-b border-gray-100 pb-2">
                                {t('shared.legal.imprint.sections.business.title')}
                            </h2>
                            <div className="space-y-4 text-gray-600">
                                <p>
                                    <strong className="text-gray-900 block mb-1">
                                        {t('shared.legal.imprint.sections.business.object_label')}
                                    </strong>
                                    {t('shared.legal.imprint.sections.business.object')}
                                </p>
                                <p>
                                    <strong className="text-gray-900 block mb-1">
                                        {t('shared.legal.imprint.sections.business.chamber_label')}
                                    </strong>
                                    {t('shared.legal.imprint.sections.business.chamber')}
                                </p>
                                <p>
                                    <strong className="text-gray-900 block mb-1">
                                        {t('shared.legal.imprint.sections.business.regulation_label')}
                                    </strong>
                                    <a href="https://www.ris.bka.gv.at" target="_blank" rel="noopener noreferrer" className="text-clever-teal hover:underline flex items-center gap-1">
                                        www.ris.bka.gv.at <ExternalLink size={12} />
                                    </a>
                                </p>
                            </div>
                        </section>

                        {/* HAFTUNGSAUSSCHLUSS & URHEBERRECHT */}
                        <section className="space-y-8 text-sm text-gray-500 leading-relaxed border-t border-gray-100 pt-8">
                            <div>
                                <h3 className="font-bold text-gray-900 mb-2">
                                    {t('shared.legal.imprint.sections.disclaimer.content_title')}
                                </h3>
                                <p>{t('shared.legal.imprint.sections.disclaimer.content_text')}</p>
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 mb-2">
                                    {t('shared.legal.imprint.sections.disclaimer.links_title')}
                                </h3>
                                <p>{t('shared.legal.imprint.sections.disclaimer.links_text')}</p>
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 mb-2">
                                    {t('shared.legal.imprint.sections.disclaimer.copyright_title')}
                                </h3>
                                <p>{t('shared.legal.imprint.sections.disclaimer.copyright_text')}</p>
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 mb-2">
                                    {t('shared.legal.imprint.sections.disclaimer.dispute_title')}
                                </h3>
                                <p>
                                    {t('shared.legal.imprint.sections.disclaimer.dispute_text').split('https://ec.europa.eu/consumers/odr').map((part, i, arr) => (
                                        <React.Fragment key={i}>
                                            {part}
                                            {i < arr.length - 1 && (
                                                <a href="https://ec.europa.eu/consumers/odr" className="text-clever-teal hover:underline">
                                                    https://ec.europa.eu/consumers/odr
                                                </a>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </p>
                            </div>
                        </section>

                    </div>
                </Reveal>
            </div>
        </div>
    );
};
